import {
  Badge,
  Spinner,
  TabItem,
  Tabs as TabsComponent,
} from '@dixa/lib-shared-habla';
import { ReactElement, useMemo } from 'react';

import { searchApi } from 'apis/search';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import type { SearchViewScope, SearchViewTab } from '../../search.types';
import { maxResultsCount } from '../../utils/results/maxResultsCount';

import styles from './searchTabs.module.scss';

type SearchTabsProps = {
  onChangeScope: (scope: SearchViewScope) => void;
};

/**
 * Represents a component that is used to render the tabs that separate search
 * results.
 */
export const SearchTabs = ({ onChangeScope }: SearchTabsProps): JSX.Element => {
  const { params } = useAppSelector((state) => state.search);
  const { scope } = useAppPathParams('/search/:scope');

  // Map the URL scope to the tab that should be selected
  const activeTab: SearchViewTab =
    scope === 'conversations' ? 'conversations' : 'contacts';

  const { count, isFetching } =
    searchApi.endpoints.readAdvancedSearchResults.useQueryState(
      {
        params,
      },
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          count: Math.min(
            data?.data.results.reduce(
              (acc, result) => (acc += result.nb_hits),
              0,
            ) ?? 0,
            maxResultsCount,
          ),
        }),
      },
    );

  const tabs = useMemo(
    (): TabItem[] =>
      Object.keys(tabsRecord).map((tab): TabItem => {
        const { label } = tabsRecord[tab as SearchViewTab];
        const showCount =
          activeTab === tab &&
          (!!params.query || params.filters.conditions.length > 0);

        const tabElement: ReactElement = (
          <>
            <span>{label}</span>

            {showCount && (
              <Badge
                variant="secondary"
                size="small"
                className={styles.tabBadge}
              >
                {isFetching ? (
                  <Spinner size={12} />
                ) : (
                  count
                    .toLocaleString(navigator.language)
                    .concat(count >= maxResultsCount ? '+' : '')
                )}
              </Badge>
            )}
          </>
        );

        return { id: tab, label: tabElement };
      }),
    [
      activeTab,
      count,
      isFetching,
      params.filters.conditions.length,
      params.query,
    ],
  );

  const handleTabChange = (tab: SearchViewTab) => {
    const { defaultScope } = tabsRecord[tab];
    onChangeScope(defaultScope);
  };

  return (
    <div className={styles.tabsContainer}>
      <TabsComponent
        tabs={tabs}
        selectedId={activeTab}
        onChange={(tab) => handleTabChange(tab as SearchViewTab)}
        tabClassName={styles.tab}
        tabSelectedClassName={styles.tabSelected}
      />
    </div>
  );
};

const tabsRecord: Record<
  SearchViewTab,
  { label: string; defaultScope: SearchViewScope }
> = {
  conversations: { label: 'Conversations', defaultScope: 'conversations' },
  contacts: { label: 'Contacts', defaultScope: 'users' },
};
